import { FC, useMemo } from 'react'
import * as styles from 'components/features/socialFeed/ComposeErrors/ComposeErrors.styl'
import { WithComposeId } from 'interfaces/feed'
import { PostComposeError } from 'components/features/pfm/PostComposeError/PostComposeError'
import { TAG_REQUIRED_ERROR, CONTENT_REQUIRED_ERROR, WITH_DESCRIPTION_ERROR, NO_FEED_USER_ERROR } from 'constants/feed'
import { parseTags } from 'helpers/composeHelpers'
import { ABORTED_STATUS_CODE, VALIDATION_ERROR_STATUS_CODE, SERVICE_UNAVAILABLE_CODE } from 'constants/ajax'
import { useComposeState } from 'helpers/hookHelpers'

const DEFAULT_COMPOSE = {
  errorCode: null,
  errorExtra: null,
  text: '',
  imagesIds: [],
  link: null,
  isLinkRemoved: null,
}

type ComposeErrorsProps =
  & WithComposeId

export const ComposeErrors: FC<ComposeErrorsProps> = (props) => {

  const composeState = useComposeState(props.composeId, DEFAULT_COMPOSE)
  const { errorCode, errorExtra, text, imagesIds, link, isLinkRemoved } = composeState

  const hasContent = useMemo(() => {
    return text || imagesIds.length || (link && !isLinkRemoved)
  }, [text, imagesIds, link, isLinkRemoved])

  const tags = parseTags(text)

  const errorOwnCode = useMemo(() => {
    switch (errorCode) {
      case TAG_REQUIRED_ERROR: {
        if (!tags.length || !tags[0]) {
          return TAG_REQUIRED_ERROR
        }
        return null
      }

      case CONTENT_REQUIRED_ERROR: {
        if (!hasContent) {
          return CONTENT_REQUIRED_ERROR
        }
        return null
      }

      case WITH_DESCRIPTION_ERROR:
      case VALIDATION_ERROR_STATUS_CODE:
      case ABORTED_STATUS_CODE: {
        return errorCode
      }

      case NO_FEED_USER_ERROR: {
        return null
      }
    }

    if (!!errorCode) {
      return SERVICE_UNAVAILABLE_CODE
    }
    return null
  }, [errorCode, hasContent, tags])

  const errorDescription = useMemo(() => {
    if (errorOwnCode !== WITH_DESCRIPTION_ERROR || !errorExtra || !errorExtra.length) {
      return null
    }

    return errorExtra.map((description, index) => (
      <div key={ index }>
        { description }
      </div>
    ))
  }, [errorExtra, errorOwnCode])

  return (
    <div className={ styles.composeErrors }>
      <PostComposeError isShown={ errorOwnCode === TAG_REQUIRED_ERROR } direction='toLeft'>
        Add a tag to complete your post
      </PostComposeError>
      <PostComposeError isShown={ errorOwnCode === CONTENT_REQUIRED_ERROR } direction='toTop'>
        Add a text or image to complete your post
      </PostComposeError>
      <PostComposeError isShown={ errorOwnCode === WITH_DESCRIPTION_ERROR && !!errorDescription }>
        { errorDescription }
      </PostComposeError>
      <PostComposeError isShown={ errorOwnCode === VALIDATION_ERROR_STATUS_CODE }>
        Wrong post
      </PostComposeError>
      <PostComposeError isShown={ errorOwnCode === SERVICE_UNAVAILABLE_CODE }>
        Server not available now. Please try again later
      </PostComposeError>
      <PostComposeError isShown={ errorOwnCode === ABORTED_STATUS_CODE }>
        No Internet connection
      </PostComposeError>
    </div>
  )
}
